import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { portalConst } from '@app/config';
import { AttachmentService } from '@app/core/services/attachment.service';
import { AttachmentTypeEnum } from '@app/modules/eclass/attachment-type.enum';
import { SHARED_MODULES } from '@app/shared';
import { FileViewerComponent } from '@app/shared/modules/file-viewer/file-viewer.component';
import { Vimeo } from '@app/shared/utils';
import { TranslateService } from '@ngx-translate/core';
import { first } from 'lodash';
import { map, take } from 'rxjs';

@Component({
  imports: [SHARED_MODULES, FileViewerComponent],
  templateUrl: './modal-announcement-read.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalAnnouncementReadComponent implements OnInit {
  protected readonly data = inject(DIALOG_DATA);
  protected readonly dialogRef = inject<DialogRef<string>>(DialogRef);
  private readonly _attachmentService = inject(AttachmentService);
  private readonly _translateService = inject(TranslateService);

  protected dataViewer = signal<any>(null);
  protected allAttachments: any[] = [];
  protected isGroupsExpanded = signal(false);
  protected isStudentsExpanded = signal(false);
  protected targetUsers: any[] = [];

  public ngOnInit(): void {
    this.initAttachments();
    this.initTargetUsers();
    this.setDataViewer(first(this.allAttachments));
  }

  private initTargetUsers() {
    const raw = this.data.communication_users ?? this.data.users ?? [];

    this.targetUsers = raw
      .map((user: any) => {
        const data = typeof user === 'object' && user !== null ? user : {};
        const user_id = Number(data.user_id);
        if (!user_id) {
          return undefined;
        }

        return {
          user_id,
          group_id: data.group_id !== undefined ? Number(data.group_id) : undefined,
          name: [data.user?.name ?? data.name ?? data.nome, data.user?.sobrenome].filter((part) => !!part).join(' '),
        };
      })
      .filter((user) => !!user);
  }

  protected studentName(user: any): string {
    return user?.name ?? '';
  }

  private initAttachments() {
    if (this.data.description) {
      this.allAttachments.push({
        id: undefined,
        file: undefined,
        title: this._translateService.instant('geral.conteudo'),
        size: undefined,
        extension: 'html',
        html: this.data.description,
      });
    }
    if (this.data.attachments?.length) {
      this.allAttachments.push(...this.data.attachments);
    }
  }

  protected onOpenAttachment(attachment: any) {
    this.setDataViewer(attachment);
  }

  protected setDataViewer(attachment: any) {
    if (!attachment) {
      this.dataViewer.set(null);
      return;
    }

    if (attachment?.type === AttachmentTypeEnum.Video) {
      const videoUrl = Vimeo.getVideoUrl(attachment);
      if (!!videoUrl) {
        this.dataViewer.set({
          name: attachment?.title,
          url: videoUrl,
        });
        return;
      }

      const params = {
        id: attachment?.id,
        video: '/videos/' + Vimeo.getVideoId(attachment),
        conta: portalConst.vimeo_account,
      };

      this._attachmentService
        .checkVideo(params)
        .pipe(
          take(1),
          map((res) => Vimeo.getVideoUrl({ ...attachment, vimeo_hash: res.vimeo_hash })),
        )
        .subscribe((url) =>
          this.dataViewer.set({
            name: attachment?.title,
            url,
          }),
        );

      return;
    }

    this.dataViewer.set({
      name: attachment?.title,
      url: attachment?.url ? attachment?.url : attachment?.file,
      html: attachment?.html,
    });
  }

  protected icon(extension): string {
    return '/assets/images/files/' + extension + '.svg';
  }
}
